import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import { MatenRequestResponse } from '../../../shared';

class KnowledgeEntryEditor {
    public entry_id: number | null = null;

    public entry_question: string = '';
    public entry_answer: string = '';
    public entry_context_url: string = '';

    constructor(entry: any) {
        makeObservable(this, {
            entry_question: observable,
            entry_answer: observable,
            entry_context_url: observable,
            question: computed,
            answer: computed,
            context_url: computed,
            questionLength: computed,
            isQuestionTooShort: computed,
            questionCharacterInfo: computed,
            answerLength: computed,
            isAnswerTooLong: computed,
            answerCharacterInfo: computed,
        });

        if (entry) {
            this.entry_id = entry.id || null;
            this.entry_question = entry.question || '';
            this.entry_answer = entry.answer || '';
            this.entry_context_url = entry.context_url || '';
        }
    }

    get isNew(): boolean {
        return this.entry_id === null;
    }

    get isValid(): boolean {
        return !this.isQuestionTooShort && !this.isAnswerTooLong;
    }

    get answer(): string {
        return this.entry_answer;
    }

    set answer(text: string) {
        // Allow any length, but track when it exceeds 600 characters
        runInAction(() => {
            this.entry_answer = text;
        });
    }

    get answerLength(): number {
        return this.entry_answer.length;
    }

    get isAnswerTooLong(): boolean {
        return this.entry_answer.length > 600;
    }

    get answerCharacterInfo(): string {
        return `${this.entry_answer.length}/600`;
    }

    get question(): string {
        return this.entry_question;
    }

    set question(text: string) {
        // Allow any length for input, but track validation
        runInAction(() => {
            this.entry_question = text;
        });
    }

    get questionLength(): number {
        return this.entry_question.length;
    }

    get isQuestionTooShort(): boolean {
        return this.entry_question.length < 3;
    }

    get questionCharacterInfo(): string {
        return `${this.entry_question.length}/80`;
    }

    get context_url(): string {
        return this.entry_context_url;
    }

    set context_url(text: string) {
        // limit to 80 characters
        // if (text.length <= 80) {
        runInAction(() => {
            this.entry_context_url = text;
        });
        // }
    }
}

/**
 * The details of every knowledge dialog store.
 */
class KnowledgeDialogStore {
    private sdk: ApplicationSdk;

    public dialogLoading: boolean = false;

    public knowledge_id: number | null = null;
    public knowledge_type: string = '';
    public knowledge_name: string = '';
    public knowledge_description: string = '';
    public knowledge_image_url: string = '';
    public knowledge_store_id: number | null = null; // Store ID for the current knowledge store
    public knowledge_entries: Array<any> = []; // List of knowledge entries

    public menu_entry_active_id: number | null = null; // Active entry ID for the menu

    public editor: KnowledgeEntryEditor | null = null; // Placeholder for the entry editor

    public selectedPdfFile: File | null = null; // Selected PDF file for upload

    public s3url: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            editor: observable,
            dialogLoading: observable,
            knowledge_name: observable,
            knowledge_description: observable,
            knowledge_type: observable,
            knowledge_image_url: observable,
            knowledge_store_id: observable,
            knowledge_entries: observable,
            menu_entry_active_id: observable,
            selectedPdfFile: observable,
            closeEditor: action,
            knowledgeName: computed,
            knowledgeDescription: computed,
            isLoading: computed,
            isNewKnowledge: computed,
            uiWide: computed,
            allEntries: computed,
            menuActiveEntry: computed,
        });
        this.sdk = sdk;
    }

    public async init(config: any): Promise<void> {
        const { type, store_id, knowledge_id } = config || {};
        runInAction(() => {
            this.knowledge_type = type || '';
            this.knowledge_store_id = store_id || null;
            this.knowledge_id = knowledge_id || null;
        });

        if (knowledge_id) {
            await this.fetchKnowledge(config.knowledge_id);
        }
    }

    public async saveKnowledge(): Promise<void> {
        runInAction(() => {
            this.dialogLoading = true;
        });

        if (this.knowledge_type === 'pdf' && !this.selectedPdfFile) {
            throw new Error('Bitte eine PDF-Datei auswählen oder den Typ ändern.');
        }

        let knowledgeSave: any = null;

        if (this.knowledge_type === 'pdf') {
            const uploadResult = await this.sdk.uploadImage(
                'knowledge',
                'module/upload',
                this.selectedPdfFile,
                {
                    timeout: 60000,
                    additionalData: {
                        store_id: this.knowledge_store_id,
                        name: this.knowledgeName,
                    },
                }
            );
            knowledgeSave = uploadResult;
        } else {
            knowledgeSave = await this.sdk.fetchApi(
                'knowledge',
                this.knowledge_id ? 'module/update' : 'module/add',
                {
                    name: this.knowledgeName,
                    description: this.knowledgeDescription,
                    type: this.knowledge_type,
                    store_id: this.knowledge_store_id,
                    knowledge_id: this.knowledge_id,
                }
            );
        }

        runInAction(() => {
            this.dialogLoading = false;
        });

        if (knowledgeSave?.status === 201) {
            runInAction(() => {
                const { knowledge_id } = knowledgeSave.json || {};
                this.knowledge_id = knowledge_id;
                // this.s3url = s3url;
            });
        }
    }

    public async fetchKnowledge(id: number | null = null): Promise<void> {
        runInAction(() => {
            this.dialogLoading = true;
        });
        const knowledgeDetails: any = await this.sdk.fetchApi('knowledge', 'module/get', {
            knowledge_id: id || this.knowledge_id,
        });

        runInAction(() => {
            this.dialogLoading = false;
        });

        if (knowledgeDetails?.status === 200) {
            runInAction(() => {
                const { s3url, knowledge } = knowledgeDetails.json || {};

                this.knowledge_type = knowledge.type || '';
                this.knowledge_description = knowledge.description || '';
                this.knowledge_name = knowledge.name || '';
                this.knowledge_entries = knowledge.entries || [];
                this.s3url = s3url;
            });
        }
    }

    public async onSaveEntry(): Promise<any> {
        if (this.editor) {
            runInAction(() => {
                this.dialogLoading = true;
            });

            const entryData = {
                question: this.editor.question,
                answer: this.editor.answer,
                context_url: this.editor.context_url || null,
                knowledge_id: this.knowledge_id,
                entry_id: this.editor.entry_id || null,
            };

            const entrySave: any = await this.sdk.fetchApi(
                'knowledge',
                this.editor.isNew ? 'module/entry/add' : 'module/entry/update',
                entryData
            );

            runInAction(() => {
                this.dialogLoading = false;
            });

            if (entrySave?.status === 200 || entrySave?.status === 201) {
                runInAction(() => {
                    // const { entry } = entrySave.json || {};
                    // this.knowledge_entries.push(entry);
                    this.closeEditor();
                });
                //@ts-ignore
                this.fetchKnowledge(this.knowledge_id);
            }
        }
    }

    public async onLearnModule(): Promise<any> {
        runInAction(() => {
            this.dialogLoading = true;
        });

        const learnModule: any = await this.sdk.fetchApi('knowledge', 'module/train', {
            knowledge_id: this.knowledge_id,
        });

        runInAction(() => {
            this.dialogLoading = false;
        });

        if (learnModule?.status !== 201 && learnModule?.status !== 200) {
            const errorCode = learnModule?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_NO_VECTOR_STORES':
                        throw new Error('Kein Vektorstore vorhanden!');
                    case 'ERR_ADD_FILE_TO_VECTOR':
                        throw new Error('Fehler beim Hinzufügen der Datei zum Vektorstore!');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public async onAddEntry(): Promise<any> {
        runInAction(() => {
            this.editor = new KnowledgeEntryEditor(null);
        });
    }

    public async onEditEntry(entry: any): Promise<any> {
        runInAction(() => {
            this.editor = new KnowledgeEntryEditor(entry);
        });
    }

    public async deleteEntry(entry_id: number): Promise<MatenRequestResponse> {
        if (entry_id) {
            const entryData = {
                entry_id,
            };
            const responseEntry = await this.sdk.fetchApi(
                'knowledge',
                'module/entry/delete',
                entryData
            );

            if (responseEntry.status === 200) {
                return { success: true, status: 200 };
            }
        }
        return { success: false, status: 404, error: 'Wissensmodul nicht gefunden' };
    }

    public closeEditor(): void {
        this.editor = null;
    }

    set knowledgeName(name: string) {
        // limit to 80 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.knowledge_name = name;
            });
        }
    }

    get knowledgeName(): string {
        return this.knowledge_name;
    }

    get imageBlob(): string {
        if (!this.knowledge_image_url) {
            return '';
        }
        return `${this.s3url}${this.knowledge_image_url}.jpeg`;
    }

    set knowledgeDescription(text: string) {
        // limit to 40 characters
        if (text.length <= 255) {
            runInAction(() => {
                this.knowledge_description = text;
            });
        }
    }

    get knowledgeDescription(): string {
        return this.knowledge_description;
    }

    get isLoading(): boolean {
        return this.dialogLoading;
    }

    get isNewKnowledge(): boolean {
        return !this.knowledge_id;
    }

    get uiWide(): boolean {
        if (this.isNewKnowledge || this.knowledge_type === 'pdf') {
            return false;
        }
        return true; // Existing knowledge dialog is wide
    }

    get saveButton(): string {
        return this.knowledge_id ? 'Aktualisieren' : 'Erstellen';
        // return '';
    }

    get header(): string {
        // return this.product_id ? 'Produkt bearbeiten' : 'Neues Produkt anlegen';
        return '';
    }

    get id(): string {
        return 'knowledge';
    }

    get allEntries(): Array<any> {
        return this.knowledge_entries || [];
    }

    get menuActiveEntry(): number | null {
        return this.menu_entry_active_id;
    }

    set menuActiveEntry(value: number | null) {
        runInAction(() => {
            this.menu_entry_active_id = value;
        });
    }
}

export default KnowledgeDialogStore;
